import { Step, StepLabel, Stepper } from '@mui/material';
import { useState } from 'react';
import UploadFile from './UploadFile';
import { UploadFileResult } from 'hooks/ui/useFileUpload';
import Settings from './Settings';
import Review from './Review';
import ErrorBoundary from 'components/ErrorBoundary';
import PageLoader from 'components/PageLoader';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { Column, Divider, Row } from 'further-ui/components/Layout';
import Button from 'components/Button';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import { usePagination } from 'further-ui/hooks';
import { SortOrder } from 'further-types/utils';

enum Steps {
  Upload,
  Settings,
  Review,
}

const stepLabels = {
  [Steps.Upload]: 'Upload',
  [Steps.Settings]: 'Import settings',
  [Steps.Review]: 'Review',
};

type Props = {
  open: boolean;
  onClose: () => void;
  bankAccountId: string;
};

const ImportStatement: React.FC<Props> = ({ open, onClose, bankAccountId }) => {
  const { selectedVehicleId } = useLayoutContext();
  const [activeStep, setActiveStep] = useState(Steps.Upload);
  const [dateFormat, setDateFormat] = useState<string>();
  const [uploadedFile, setUploadedFile] = useState<UploadFileResult | null>(
    null,
  );

  const pagination = usePagination({ order: SortOrder.Asc, orderBy: 'date' });

  const commonQueryParams = {
    bankAccountId,
    vehicleId: selectedVehicleId ?? '',
    dateFormat: dateFormat ?? '',
    bankStatementFilePath: uploadedFile?.filePath ?? '',
  };

  const previewParams =
    activeStep === Steps.Review
      ? { ...pagination.properties, orderBy: 'date' as const }
      : {
          page: 1,
          rowsPerPage: 1,
          order: SortOrder.Asc,
          orderBy: 'date' as const,
        };

  const { data: previewData, isLoading: isLoadingPreview } =
    api.bankStatement.getPreview.useQuery(
      { ...commonQueryParams, ...previewParams },
      {
        enabled: activeStep !== Steps.Upload && !!dateFormat && !!uploadedFile,
      },
    );

  const settingsCanProceed = !!dateFormat && !!previewData?.totalCount;
  const reviewCanComplete =
    !previewData?.totalErrors && !!previewData?.operations?.length;

  const handleClose = () => {
    setActiveStep(Steps.Upload);
    setDateFormat(undefined);
    setUploadedFile(null);
    onClose();
  };

  const importBankStatement = api.bankStatement.import.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Bank statement successfully imported',
      onSuccess: handleClose,
      invalidations: [
        getQueryKey(api.bankStatement),
        getQueryKey(api.bankAccount.listBankAccountOperations),
      ],
    }),
  );

  const handleNextStep = () => {
    setActiveStep(activeStep + 1);
  };

  const handlePreviousStep = () => {
    setActiveStep(activeStep - 1);
  };

  const handleComplete = () => {
    if (!uploadedFile || !dateFormat) return;
    importBankStatement.mutate({
      vehicleId: selectedVehicleId ?? '',
      bankStatementFilePath: uploadedFile.filePath,
      bankAccountId,
      dateFormat,
    });
  };

  return (
    <FullPageDrawer
      open={open}
      onClose={handleClose}
      title="Upload bank statement"
      actions={
        <Column fullWidth spacing="lg">
          <Divider />
          <Row justify="space-between">
            <Button
              variant="outlined"
              size="large"
              onClick={
                activeStep === Steps.Upload ? handleClose : handlePreviousStep
              }
              disabled={importBankStatement.isPending}
            >
              {activeStep === Steps.Upload ? 'Cancel' : 'Back'}
            </Button>
            {activeStep === Steps.Upload && (
              <Button
                variant="contained"
                disabled={!uploadedFile}
                onClick={handleNextStep}
                size="large"
              >
                Next
              </Button>
            )}
            {activeStep === Steps.Settings && (
              <Button
                variant="contained"
                disabled={!settingsCanProceed}
                onClick={handleNextStep}
                size="large"
              >
                Next
              </Button>
            )}
            {activeStep === Steps.Review && (
              <Button
                variant="contained"
                disabled={!reviewCanComplete}
                onClick={handleComplete}
                loading={importBankStatement.isPending}
                size="large"
              >
                Complete import
              </Button>
            )}
          </Row>
        </Column>
      }
      size="md"
      innerScroll
    >
      <ErrorBoundary loader={<PageLoader />}>
        <Column fullWidth spacing="lg">
          <Stepper activeStep={activeStep}>
            {Object.values(stepLabels).map((stepLabel) => {
              return (
                <Step key={stepLabel}>
                  <StepLabel>{stepLabel}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          <Divider />
          {activeStep === Steps.Upload && (
            <UploadFile
              onFileUpload={setUploadedFile}
              uploadedFile={uploadedFile ?? undefined}
              bankAccountId={bankAccountId}
            />
          )}
          {activeStep === Steps.Settings && !!uploadedFile && (
            <Settings
              dateFormat={dateFormat}
              onDateFormatChange={setDateFormat}
              operationPreview={previewData?.operations[0]}
              isLoadingPreview={isLoadingPreview}
            />
          )}
          {activeStep === Steps.Review && !!uploadedFile && !!dateFormat && (
            <Review
              operations={previewData?.operations ?? []}
              totalCount={previewData?.totalCount ?? 0}
              totalErrors={previewData?.totalErrors ?? 0}
              pagination={pagination}
            />
          )}
        </Column>
      </ErrorBoundary>
    </FullPageDrawer>
  );
};

export default ImportStatement;
